import React, {useCallback, useState} from 'react';
import clsx from 'clsx';
import {Image, Link} from '@shopify/hydrogen';

import {
  getProductPromoHeadlineFromTag,
  getProductLabelFromTag,
  getNumberID,
} from '~/lib/utils';
import {
  CurrencyCode,
  type MoneyV2,
  type Product,
  type ProductVariant,
} from '@shopify/hydrogen/storefront-api-types';
import {Text} from '~/components';
import {TEXT_VARIANTS} from '../elements/Text';
import {VariantSelector} from '../product/VariantSelector';
import {ProductPriceDisplay} from '../product/ProductPriceDisplay.client';
import {ANALYTIC_EVENT_NAMES, DATA_TEST_IDS} from '~/lib/const';

type PromoHeadLine = {
  key: string;
  value: string;
  order?: number;
  color: string;
};

const getColorVariants = (variants: ProductVariant[]) => {
  const colorImages = {} as any;

  variants.forEach((variant) => {
    if (Object.keys(colorImages).length >= 10) {
      return;
    }

    const color = variant.selectedOptions.find(({name, value}) => {
      return name.toLowerCase() === 'color';
    })?.value;
    if (color == null) return;
    if (colorImages[color] == null && variant.image != null) {
      colorImages[color] = variant;
    }
  });

  return colorImages;
};

function PromotionalHeadline({data}: {data: PromoHeadLine[]}) {
  return (
    <div className={'grid gap-2.5'}>
      {data.map(({value, order, color}) => {
        return (
          <div
            data-testid={DATA_TEST_IDS.PROMOTIONAL_HEADLINE}
            key={value + order}
            className={'flex'}
          >
            <Text
              variant={TEXT_VARIANTS.NO_STYLE}
              className={
                'text-black md:text-16/20 font-bold font-header text-14/16'
              }
              style={{color}}
            >
              {value}
            </Text>
          </div>
        );
      })}
    </div>
  );
}

const imageLoaderOptions = {
  crop: 'center' as const,
  scale: 2 as const,
  width: 320,
  height: 320,
};

export function ProductCard({
  product,
  className,
  loading,
  onClick,
}: {
  product: Product;
  className?: string;
  loading?: HTMLImageElement['loading'];
  onClick?: () => void;
}) {
  const [currentVariantId, setCurrentVariantId] = useState<string | null>(null);
  const [hoveredVariantId, setHoveredVariantId] = useState<string>();
  const {tags} = product;
  const cardLabel = getProductLabelFromTag(tags);
  const promoHeadlines = getProductPromoHeadlineFromTag(tags);
  const variants = product.variants?.nodes;

  const availableSkus: any[] = [];
  product.metafields?.forEach((m: any) => {
    if (m.key == 'variants_discontinued_by_sku') {
      const skus = JSON.parse(m.value);
      Object.keys(skus).forEach((sku) => {
        if (skus[sku] == false) {
          availableSkus.push(sku);
        }
      });
    }
  });

  const available: any = variants.find((v) => {
    return availableSkus.indexOf(v.sku) >= 0;
  });

  const currentVariant =
    currentVariantId == null && hoveredVariantId == null
      ? undefined
      : variants.find((v) => {
          if (hoveredVariantId != null) {
            return v.id === hoveredVariantId;
          }
          return v.id === currentVariantId;
        });
  const {priceV2: price, compareAtPriceV2: compareAtPrice} =
    currentVariant ?? variants[0];

  const styles = clsx(
    'relative border border-gray-100 bg-gray-50/25 h-full',
    className,
  );

  const variantsColorObject = getColorVariants(variants) as {
    [key: string]: ProductVariant;
  };

  const variantsColorArray =
    Object.values(variantsColorObject).length <= 1
      ? [variants[0]]
      : Object.values(variantsColorObject);

  const handleHoverVariant = useCallback(
    (
      event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>,
      id: string,
      isHover: boolean,
    ) => {
      if (isHover) {
        setHoveredVariantId(id);
      } else {
        setHoveredVariantId(undefined);
      }
    },
    [],
  );

  const selectedColorOption =
    currentVariant != null
      ? currentVariant.selectedOptions.find(({name}) => {
          return name.toLowerCase() === 'color';
        })?.value
      : null;
  const selectedColorOptionUrl =
    selectedColorOption != null
      ? `?color=${selectedColorOption.toLowerCase()}`
      : '';

  const handleClick = useCallback(() => {
    // Select item event don't need to be sent after page load
    // @ts-ignore
    gtag({
      _clear: true,
      event: ANALYTIC_EVENT_NAMES.SELECT_ITEM,
      ecommerce: {
        items: [
          {
            item_name: product.title,
            item_id: getNumberID(product.id),
            item_brand: product.vendor,
            currency: 'USD',
            price: product.variants?.nodes?.[0]?.priceV2?.amount ?? 0,
            item_category: product.productType,
            item_variant: product.variants?.nodes?.[0]?.title ?? product.title,
          },
        ],
      },
    });

    onClick?.();
  }, [onClick, product]);

  // function hasTag(product: any, tags: any) {
  //   for (let i = 0; i < tags.length; i++) {
  //     if (product.tags.indexOf(tags[i]) >= 0) return true;
  //   }

  //   return false;
  // }

  const priceVariant = currentVariant || variants[0];
  product.metafields?.forEach((m) => {
    if (m.key == 'online_variant_prices') {
      const onlinePrices = JSON.parse(m.value);
      const keys = Object.keys(onlinePrices);
      keys.forEach((key) => {
        if (key == priceVariant.sku) {
          const onlinePrice = onlinePrices[key];
          const onlineAmount = parseFloat(onlinePrice);
          if (onlineAmount) price.amount = onlineAmount.toString();
        }
      });
    }
  });

  if (product.meta?.custom?.online_variant_lowest_price?.amount) {
    const onlineAmount = parseFloat(
      product.meta?.custom?.online_variant_lowest_price?.amount,
    );
    if (onlineAmount) price.amount = onlineAmount.toString();
  }

  const discount = 0;
  // let discountPercent = 0;
  // if (product.tags.indexOf('sale-additional:10') >= 0) {
  //   discountPercent = 10;
  // }
  // if (product.tags.indexOf('2024-price-drop') >= 0) {
  //   discountPercent = 20;
  // }
  // const priceDropTags = product.tags.filter((tag) =>
  //   tag.startsWith('2024-price-drop:'),
  // );
  // if (priceDropTags.length > 0) {
  //   discountPercent = Math.max(
  //     ...priceDropTags.map((tag) =>
  //       parseFloat(tag.replace('2024-price-drop:', '')),
  //     ),
  //   );
  // }

  // if (discountPercent) {
  //   discount = Math.round(Number(price.amount) * discountPercent) / 100;
  // }

  const discountedPrice: MoneyV2 = {
    amount: (Number(price.amount) - discount).toString(),
    currencyCode: CurrencyCode.Usd,
  };

  // const isTrigger = false;
  // const is2024 = product.tags.indexOf('year:2024') >= 0;
  // const isInvalid = false;
  // is2024 ||
  // product.tags.indexOf('Cat-Packages') >= 0 ||
  // product.tags.indexOf('Cat-Packages:Alpine') >= 0 ||
  // product.tags.indexOf('Cat-Packages:XC') >= 0 ||
  // product.tags.indexOf('Cat-Packages:Snowboard') >= 0 ||
  // product.tags.indexOf('Cat-PackagesAlpine:Recreational') >= 0 ||
  // product.tags.indexOf('Cat-PackagesAlpine:Recreational-Plus') >= 0 ||
  // product.tags.indexOf('Cat-PackagesAlpine:Performance') >= 0 ||
  // product.tags.indexOf('Cat-PackagesAlpine:Freestyle/Twin') >= 0 ||
  // product.tags.indexOf('Cat-PackagesAlpine:Juniors') >= 0 ||
  // product.tags.indexOf('Cat-PackagesXC:Classic') >= 0 ||
  // product.tags.indexOf('Cat-PackagesXC:Skate') >= 0 ||
  // product.tags.indexOf('Cat-PackagesXC:Backcountry') >= 0 ||
  // product.tags.indexOf('Cat-PackagesXC:Juniors') >= 0 ||
  // product.tags.indexOf('Cat-PackagesSnowboard:Recreational') >= 0 ||
  // product.tags.indexOf('Cat-PackagesSnowboard:Recreational-Plus') >= 0 ||
  // product.tags.indexOf('Cat-PackagesSnowboard:Performance') > 0;

  // const onSale = false;
  const qualifying = product.tags.indexOf('2024-FREE-GIFT-QUALIFYING') >= 0;
  const dpEligible =
    product.tags.indexOf('year:2024') >= 0 &&
    product.title.indexOf('2024') >= 0;
  // const giveaway = product.tags.indexOf('Dec-2023-Free-Gifts') >= 0;
  // const onSale =
  //   !is2024 &&
  //   (product.tags.indexOf('Cat-Apparel') >= 0 ||
  //     product.tags.indexOf('Cat-Accessories') >= 0);

  // const promo = product.tags.indexOf('23-BLACK-FRIDAY') >= 0;
  return (
    <div className={styles} data-testid={DATA_TEST_IDS.PRODUCT_CARD}>
      <Link
        className="relative"
        onClick={handleClick}
        to={`/products/${product.handle}${selectedColorOptionUrl}`}
      >
        <div className="aspect-square overflow-hidden relative">
          {!loading && (qualifying || dpEligible) && (
            <div></div>
            // <div
            //   data-testid={DATA_TEST_IDS.PRODUCT_CARD_LABEL_VALUE(
            //     discount.toString(),
            //   )}
            //   className="absolute top-2.5 left-2.5 md:top-5 md:left-5 py-2 px-4 text-11/13 md:text-13/15 z-10 font-bold font-header bg-blue-500 rounded-full text-white uppercase"
            //   style={{
            //     background: qualifying ? 'rgb(179, 0, 0)' : 'rgb(179, 0, 0)',
            //   }}
            // >
            //   {/* {compareAtPrice?.amount && compareAtPrice?.amount != price.amount
            //     ? 'Save an extra $' + discount.toFixed(2)
            //     : 'Save $' + discount.toFixed(2)} */}
            //   {qualifying ? 'Holiday Gift Giveaway' : 'Dec 2X Loyalty Pts'}
            // </div>
          )}

          {/* {!loading && giveaway && (
            <div
              data-testid={DATA_TEST_IDS.PRODUCT_CARD_LABEL_VALUE(
                discount.toString(),
              )}
              className="absolute top-2.5 left-2.5 md:top-5 md:left-5 py-2 px-4 text-11/13 md:text-13/15 z-10 font-bold font-header bg-blue-500 rounded-full text-white uppercase"
              style={{
                background: 'rgb(179, 0, 0)',
              }}
            >
              Holiday Gift Giveaway
            </div>
          )} */}
          {/* {promo && (
            <div
              data-testid={DATA_TEST_IDS.PRODUCT_CARD_LABEL_VALUE(
                discount.toString(),
              )}
              className="absolute top-2.5 left-2.5 md:top-5 md:left-5 py-2 px-4 text-11/13 md:text-13/15 z-10 font-bold font-header bg-blue-500 rounded-full text-white uppercase"
            >
              {compareAtPrice?.amount && compareAtPrice?.amount != price.amount
                ? 'Save In-Cart'
                : 'Save In-Cart'}
            </div>
          )} */}
          {/* {onSale && (
            <div
              data-testid={DATA_TEST_IDS.PRODUCT_CARD_LABEL_VALUE(
                discount.toString(),
              )}
              style={{
                backgroundColor: 'rgb(179, 0, 0)',
              }}
              className="absolute top-2.5 left-2.5 md:top-5 md:left-5 py-2 px-4 text-11/13 md:text-13/15 z-10 font-bold font-header rounded-full text-white uppercase"
            >
              Save an Extra ${(price.amount * 0.15).toFixed(2)}
            </div>
          )} */}
          {!loading && cardLabel && (
            <div
              className="absolute top-2.5 left-2.5 md:top-5 md:left-5 p-[7px] md:p-2.5 text-11/13 md:text-13/15 z-10 font-bold font-header bg-white rounded-[5px]"
              style={{
                color: cardLabel.color,
                backgroundColor: cardLabel.bgColor,
              }}
            >
              {cardLabel.value}
            </div>
          )}
          {variantsColorArray.map((variant) => {
            const image =
              currentVariant == null
                ? available?.image || product.featuredImage
                : currentVariant.image;
            const isSelected =
              currentVariant == null || variant.id === currentVariant.id;

            if (image == null) {
              return (
                <div
                  key={variant.id}
                  className={
                    'aspect-square flex justify-center items-center font-header font-bold'
                  }
                >
                  {product.title}
                </div>
              );
            }
            return (
              <Image
                data-testid={DATA_TEST_IDS.PRODUCT_CARD_VARIANTS_COLOR}
                key={variant.id}
                className={clsx(
                  'aspect-square w-full object-cover select-none md:hover:scale-110 transition-all duration-300 ease-in border-b border-gray-50',
                  !isSelected && 'hidden',
                )}
                widths={[160]}
                sizes="160px"
                loaderOptions={imageLoaderOptions}
                // @ts-ignore Stock type has `src` as optional
                data={image}
                alt={image?.altText || product.title}
                loading={'lazy'}
              />
            );
          })}
        </div>
      </Link>
      <div className="p-6">
        <Link
          className="relative"
          onClick={handleClick}
          to={`/products/${product.handle}${selectedColorOptionUrl}`}
        >
          <Text
            testid={DATA_TEST_IDS.PRODUCT_CARD_TITLE}
            className="w-full overflow-hidden text-ellipsis text-sm font-bold line-clamp-2 h-[40px] md:h-[54px] max-w-[318px]"
            as="h5"
          >
            {product.title}
          </Text>
        </Link>
        <div className={'mt-2.5'}>
          {discount > 0 ? (
            <ProductPriceDisplay
              price={discountedPrice}
              compareAtPrice={
                compareAtPrice && compareAtPrice?.amount
                  ? compareAtPrice
                  : price
              }
            />
          ) : (
            <ProductPriceDisplay
              price={discountedPrice}
              compareAtPrice={compareAtPrice}
            />
          )}
        </div>
        {Object.keys(variantsColorObject).length > 1 && (
          <VariantSelector
            activeVariant={currentVariant}
            variantsColorObject={variantsColorObject}
            onSelect={setCurrentVariantId}
            onHoverAction={handleHoverVariant}
            availableSkus={availableSkus}
          />
        )}
        {/* {(qualifying || giveaway) && (
          <div
            data-testid={DATA_TEST_IDS.PROMOTIONAL_HEADLINE}
            className={'flex'}
          >
            <Text
              className={
                'text-black md:text-16/20 font-bold font-header text-14/16'
              }
              style={{color: 'rgb(197, 0, 0)', paddingTop: '20px'}}
            >
              {qualifying ? 'Qualifying Product' : 'Eligible Free Gift'}
            </Text>
          </div>
        )} */}
        {promoHeadlines && promoHeadlines!.length > 0 && (
          <div className={'mt-2.5 md:mt-5'}>
            <PromotionalHeadline data={promoHeadlines!} />
          </div>
        )}
      </div>
    </div>
  );
}
